// packages/web-ui/src/components/slash-palette/GlobalPalette.tsx
//
// Story W2-S7 AC #7 — centred-modal global slash palette opened on `Ctrl+P`.
// Hosts the same `<SlashContent>` surface as the inline menu but with a
// dim-backdrop modal chrome.

import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SlashContent } from './SlashContent.js';
import type { SlashCommand, SlashDispatchContext } from './commands.js';

export interface GlobalPaletteProps {
  open: boolean;
  ctx: SlashDispatchContext;
  onClose: () => void;
  /** Optional merged command source (static + skill-derived entries). */
  commands?: ReadonlyArray<SlashCommand>;
}

export function GlobalPalette(props: GlobalPaletteProps): JSX.Element | null {
  const { open, ctx, onClose, commands } = props;
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onDocKeyDown);
    return (): void => {
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      data-testid="global-palette-backdrop"
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[15vh]"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="global-palette"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={handleKeyDown}
        className="border-taupe-500 bg-cream-50 flex w-[560px] max-w-[90vw] flex-col overflow-hidden rounded-none border"
      >
        <SlashContent
          mode="modal"
          query={query}
          onQueryChange={setQuery}
          ctx={ctx}
          onClose={onClose}
          commands={commands}
        />
      </div>
    </div>
  );
}
